import React, { useState, useCallback, useEffect } from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { ReactComponent as DropdownArrow } from '../assets/DropdownArrow.svg';
import DialogTransition from './Common/DialogTransition';
import useOrganization from '../hooks/UseOrganization';
import REGEX from '../const/Regex';

const MAX_URL_LENGTH = 2048;
const MAX_DESCRIPTION_LENGTH = 2048;

/**
 * @description ApplicationTabDialog component handles the dialog for creating or editing applications.
 * @param {boolean} open - Determines if the dialog is open.
 * @param {function} setOpen - Function to set the dialog open state.
 * @param {string} mode - Mode of the dialog, either "create" or "edit".
 * @param {object} editApplication - The application data to be edited.
 * @param {function} setMode - Function to set the mode of the dialog.
 * @param {function} setApplicationList - Function to update the application list.
 * @example
 * <ApplicationTabDialog
 *   open={open}
 *   setOpen={setOpen}
 *   mode={mode}
 *   editApplication={editApplication}
 *   setMode={setMode}
 *   setApplicationList={setApplicationList}
 * />
 */
const ApplicationTabDialog = ({
  open,
  setOpen,
  mode,
  editApplication,
  setMode,
  setApplicationList,
  pageIndex,
  pageSize,
  searchTerm,
  setPageCount,
  setTotalCount,
}) => {
  const {
    updateApplication,
    createApplication,
    getApplicationList,
    getProductList,
  } = useOrganization();
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();
  const [isActive, setIsActive] = useState(true);
  const [productList, setProductList] = useState([]);

  useEffect(() => {
    if (mode === 'create') {
      getProductList(orgName).then((res) => {
        setProductList(res?.data);
      });
    }
  }, []);

  const initialValues = {
    product: {
      label: '',
      id: '',
    },
    rootUrl: '',
    redirectUrl: '',
    adRedirectUrl: '',
    description: '',
  };

  const validationSchema = object({
    product: object().shape({
      label: string().required('Required'),
      id: string().required('Required'),
    }),
    rootUrl: string()
      .matches(REGEX.URL, "Root URL isn't valid")
      .min(0)
      .max(MAX_URL_LENGTH, 'Root URL must be less than or equal to 2048')
      .required('Required'),
    redirectUrl: string()
      .matches(REGEX.URL, "Redirect URL isn't valid")
      .max(MAX_URL_LENGTH, 'Redirect URL must be less than or equal to 2048')
      .required('Required'),
    adRedirectUrl: string()
      .matches(REGEX.URL, "AD Redirect URL isn't valid")
      .max(2048, 'AD Redirect URL must be less than or equal to 2048'),
    description: string()
      .min(10, 'Description must be greater than or equal to 10 words')
      .max(
        MAX_DESCRIPTION_LENGTH,
        'Description must be less than or equal to 2048 words'
      ),
  });

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (formData) => {
      // Handle form submission with formData
      const payload = {
        is_active: isActive,
        root_url: formData?.rootUrl,
        name: formData?.product?.label,
        redirect_url: formData?.redirectUrl,
        description: formData?.description,
      };
      const editPayload = {
        is_active: isActive,
        root_url: formData?.rootUrl,
        name: formData?.product?.label,
        description: formData?.description,
        redirect_url: formData?.redirectUrl,
        ad_redirect_url: formData?.adRedirectUrl,
      };

      if (mode === 'edit') {
        updateApplication(editPayload, editApplication?.id, orgName).then(
          () => {
            setMode('create');
            getApplicationList(orgName, pageIndex, pageSize, searchTerm).then(
              (resp) => {
                setApplicationList(resp?.data);
                setPageCount(resp?.max_pages);
                setTotalCount(resp?.total_count);
              }
            );
          }
        );
      } else {
        createApplication(payload, orgName).then(() => {
          getApplicationList(orgName, pageIndex, pageSize, searchTerm).then(
            (resp) => {
              setApplicationList(resp?.data);
              setPageCount(resp?.max_pages);
              setTotalCount(resp?.total_count);
            }
          );
        });
      }
      setOpen(false);
      formikForm.handleReset();
    },
  });

  useEffect(() => {
    if (mode === 'edit') {
      formikForm.setValues({
        rootUrl: editApplication?.root_url,
        product: {
          label: editApplication?.name,
          id: editApplication?.id,
        },
        organization_id: editApplication?.organization?.id,
        redirectUrl: editApplication?.redirect_url,
        adRedirectUrl: editApplication?.ad_redirect_url,
        description: editApplication?.description,
      });
      setIsActive(editApplication?.is_active);
    }
  }, [mode]);

  /**
   * @function handleChange
   * @description Handles change events for form fields.
   * @param {object} e - The event object.
   * @returns {void}
   * @example handleOnchange(e)
   */
  const handleChange = useCallback(
    (e) => {
      formikForm.handleChange(e);
    },
    [formikForm]
  );

  /**
   * @function handleReaderInput
   * @description handles the input field for the product selection in the form.
   * @param {object} params - The parameters for the Autocomplete component.
   * @returns {JSX} - renders The TextField component for Autocomplete
   * @example handleReaderInput(params)
   */
  const handleReaderInput = useCallback(
    (params) => {
      return (
        <TextField
          required
          {...params}
          name="product"
          label="Product"
          fullWidth
          placeholder="Select product"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.product?.id && formikForm?.touched?.product
              ? formikForm.errors.product?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.product?.id) &&
            formikForm?.touched?.product
          }
        />
      );
    },
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    setOpen((prev) => !prev);
    setMode('create');
    formikForm.handleReset();
  }, [setOpen, setMode, formikForm]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>
        {mode === 'edit' ? 'Edit Application' : 'Create Application'}
      </DialogTitle>
      <IconButton
        size="large"
        onClick={handleOnClose}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          {/* Product */}
          <Grid item xs={12}>
            <Autocomplete
              disabled={mode === 'edit'}
              options={
                productList?.length > 0
                  ? productList?.map((item) => ({
                      id: item?.id,
                      label: item?.name,
                    }))
                  : []
              }
              popupIcon={<DropdownArrow />}
              noOptionsText="No product available."
              disableClearable={true}
              getOptionLabel={(item) => (item?.label ? item?.label : '')}
              isOptionEqualToValue={(option, val) =>
                option?.label === val?.label
              }
              value={formikForm.values.product}
              onChange={(_e, newValue) => {
                formikForm.setFieldValue('product', newValue);
              }}
              renderInput={handleReaderInput}
            />
          </Grid>
          {/* Root URL */}
          <Grid item xs={12}>
            <TextField
              required
              label="Root URL"
              fullWidth
              name="rootUrl"
              placeholder="Enter root URL"
              value={formikForm.values.rootUrl}
              onChange={handleChange}
              aria-label="rootUrl"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.rootUrl && formikForm?.touched?.rootUrl
                  ? formikForm?.errors?.rootUrl
                  : null
              }
              error={
                Boolean(formikForm?.errors?.rootUrl) &&
                formikForm?.touched?.rootUrl
              }
            />
          </Grid>
          {/* Redirect URL */}
          <Grid item xs={12}>
            <TextField
              required
              label="Redirect URL"
              fullWidth
              placeholder="Enter redirect URL"
              name="redirectUrl"
              value={formikForm.values.redirectUrl}
              onChange={handleChange}
              aria-label="redirectUrl"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.redirectUrl &&
                formikForm?.touched?.redirectUrl
                  ? formikForm?.errors?.redirectUrl
                  : null
              }
              error={
                Boolean(formikForm?.errors?.redirectUrl) &&
                formikForm?.touched?.redirectUrl
              }
            />
          </Grid>
          {/* AD Redirect URL */}
          <Grid item xs={12}>
            <TextField
              label="AD Redirect URL"
              fullWidth
              placeholder="Enter AD redirect URL"
              name="adRedirectUrl"
              value={formikForm.values.adRedirectUrl}
              onChange={handleChange}
              aria-label="AD Redirect URL"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.adRedirectUrl &&
                formikForm?.touched?.adRedirectUrl
                  ? formikForm?.errors?.adRedirectUrl
                  : null
              }
              error={
                Boolean(formikForm?.errors?.adRedirectUrl) &&
                formikForm?.touched?.adRedirectUrl
              }
            />
          </Grid>
          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              placeholder="Enter description"
              name="description"
              value={formikForm.values.description}
              onChange={handleChange}
              aria-label="description"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.description &&
                formikForm?.touched?.description
                  ? formikForm?.errors?.description
                  : null
              }
              error={
                Boolean(formikForm?.errors?.description) &&
                formikForm?.touched?.description
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
        title="Reset"
          variant="outlined"
          onClick={() => {
            setIsActive(true);
            formikForm.handleReset();
            if (mode === 'edit') {
              formikForm.setFieldValue('product', {
                label: editApplication?.name,
                id: editApplication?.id,
              });
            }
          }}
        >
          Reset
        </Button>

        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ApplicationTabDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  mode: PropTypes.oneOfType([PropTypes.object]).isRequired,
  editApplication: PropTypes.oneOfType([PropTypes.object]).isRequired,
  setMode: PropTypes.func.isRequired,
  setApplicationList: PropTypes.func.isRequired,
  pageIndex: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setPageCount: PropTypes.func.isRequired,
  setTotalCount: PropTypes.func.isRequired,
};
export default ApplicationTabDialog;
